import { FC } from 'react';
import { CustomModal } from '@mybonum/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Flex } from 'antd';

import { normalizeToApi } from '../../utils/normalize-to-api.ts';
import { AdminForm, TAdminFormValues } from '../form';

import { FORM_ID } from './constants.ts';
import { TAdminUpdateProps } from './types.ts';

import { useMessage } from '@/hooks/use-message.ts';
import { API } from '@/services/api';

export const AdminUpdate: FC<TAdminUpdateProps> = ({ id, initialValues, open, onClose }) => {
    const queryClient = useQueryClient();
    const { message } = useMessage();

    const { mutateAsync: updateAdmin, isPending } = useMutation({
        mutationFn: (payload: Parameters<typeof API.ADMINS.updateAdmin>[1]) =>
            API.ADMINS.updateAdmin(id, payload),
        onError: async (error) => {
            await message.error(error.message || 'Something went wrong...');
        },
    });

    const handleSubmit = async (values: TAdminFormValues): Promise<void> => {
        await updateAdmin(normalizeToApi(values));
        await queryClient.invalidateQueries({ queryKey: API.ADMINS.getAdmins().queryKey });
        onClose();
        message.success(`Admin updated successfully`);
    };

    return (
        <div>
            <CustomModal
                width={360}
                open={open}
                padding="small"
                onCancel={onClose}
                title="Update admin"
                destroyOnHidden
                footer={
                    <Flex gap={16} justify="space-between">
                        <Button danger onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="primary" htmlType="submit" form={FORM_ID} loading={isPending}>
                            Update
                        </Button>
                    </Flex>
                }
            >
                <AdminForm initialValues={initialValues} onFinish={handleSubmit} id={FORM_ID} />
            </CustomModal>
        </div>
    );
};
